import { useState } from 'react';
import { api } from 'utils';

// 참깨 연동 에러 코드
// SS00003(503): 참깨 벤더 일시 불가 / SS00002(500): 기기 위치정보 없음
const ERROR_CODE = {
  VENDOR_UNAVAILABLE: 'SS00003',
  DEVICE_LOCATION_MISSING: 'SS00002',
};

const VENDOR_UNAVAILABLE_MESSAGE =
  '참깨 연결이 원활하지 않아요. 바디코디 고객센터로 문의해 주세요.';
const DEVICE_LOCATION_MISSING_MESSAGE =
  '출입문 설정 확인이 필요해요. 바디코디 고객센터로 문의해 주세요.';

// 에러 응답(503/500) 시 errorCode별 프론트 고정 문구 결정 (백엔드 message 미노출)
const resolveActionErrorMessage = (error, fallbackMessage) => {
  const errorCode = error?.response?.data?.errorCode;

  if (errorCode === ERROR_CODE.VENDOR_UNAVAILABLE) return VENDOR_UNAVAILABLE_MESSAGE;
  if (errorCode === ERROR_CODE.DEVICE_LOCATION_MISSING) return DEVICE_LOCATION_MISSING_MESSAGE;

  return fallbackMessage;
};

const useKioskControl = (seqPartner, seqPartnerBranch) => {
  const [kiosks, setKiosks] = useState([]);
  const [doorMessage, setDoorMessage] = useState({});
  const [keepOpenLoading, setKeepOpenLoading] = useState({});
  const [doorLoading, setDoorLoading] = useState(false);

  const updateDoorMessage = (kioskName, updates) => {
    setDoorMessage((prev) => ({
      ...prev,
      [kioskName]: {
        ...prev[kioskName],
        ...updates,
      },
    }));
  };

  const getKioskList = async () => {
    try {
      const { data } = await api.get(`/kiosk/list`, {
        params: { seqPartner, seqPartnerBranch: seqPartnerBranch || 0 },
      });
      setKiosks(data || []);
    } catch (error) {
      console.error('키오스크 리스트 불러오기 실패:', error);
      setKiosks([]); // 실패 시 빈 배열
    }
  };

  const handleOpenDoor = async (kioskName) => {
    try {
      setDoorLoading(true);
      const { data } = await api.get(`/kiosk/open`, {
        params: {
          seqPartner,
          seqPartnerBranch: seqPartnerBranch || 0,
          kioskName,
        },
      });

      const now = new Date();
      const timeString = now.toLocaleTimeString('ko-KR', {
        hour: '2-digit',
        minute: '2-digit',
      });

      if (data.code === 'success') {
        updateDoorMessage(kioskName, {
          statusMessage: `출입문이 열렸습니다 (시간: ${timeString})`,
          statusType: 'success',
        });
        return;
      }

      // 통신은 성공(200)했지만 참깨가 처리 거절(code:"fail") → 참깨가 준 사유 message 그대로 표시
      updateDoorMessage(kioskName, {
        statusMessage: data.message || '출입문을 열 수 없어요. 다시 시도해 주세요.',
        statusType: 'error',
      });
    } catch (error) {
      console.error('출입문 열기 실패:', error);
      updateDoorMessage(kioskName, {
        statusMessage: resolveActionErrorMessage(
          error,
          '출입문을 열 수 없어요. 다시 시도해 주세요.'
        ),
        statusType: 'error',
      });
    } finally {
      setDoorLoading(false);
    }
  };

  const handleToggleAlwaysOpen = async (kiosk) => {
    const kioskName = kiosk.kioskName;

    // 상태 확인 불가(isKeepOpen == null)면 토글 방향을 정할 수 없어 차단 (UI에서도 비활성)
    if (kiosk.isKeepOpen == null) return;

    if (keepOpenLoading[kioskName]) return;

    setKeepOpenLoading((prev) => ({ ...prev, [kioskName]: true }));
    updateDoorMessage(kioskName, {
      isKeepOpen: !kiosk.isKeepOpen,
      statusMessage: '반영 중입니다...',
      statusType: 'loading',
    });

    try {
      const endpoint = kiosk.isKeepOpen ? '/kiosk/close' : '/kiosk/keep-open';
      const { data } = await api.post(endpoint, {
        seqPartner,
        seqPartnerBranch: seqPartnerBranch || 0,
        kioskName,
      });

      if (data.code === 'success') {
        updateDoorMessage(kioskName, {
          statusMessage: `상시 문열기 설정이 ${kiosk.isKeepOpen ? '해제' : '완료'}되었습니다.`,
          statusType: 'success',
        });
        return;
      }

      // 통신은 성공(200)했지만 참깨가 처리 거절(code:"fail") → 참깨가 준 사유 message 그대로 표시
      updateDoorMessage(kioskName, {
        isKeepOpen: kiosk.isKeepOpen,
        statusMessage: data.message || '상시 문열기 설정에 실패하였습니다. 다시 시도해 주세요.',
        statusType: 'error',
      });
    } catch (error) {
      console.error('키오스크 상시 문열기 설정 실패:', error);
      updateDoorMessage(kioskName, {
        isKeepOpen: kiosk.isKeepOpen,
        statusMessage: resolveActionErrorMessage(
          error,
          '상시 문열기 설정에 실패하였습니다. 다시 시도해 주세요.'
        ),
        statusType: 'error',
      });
    } finally {
      setKeepOpenLoading((prev) => ({ ...prev, [kioskName]: false }));
      // 벤더 2회 호출 구조 → 실패로 떠도 실제 설정됐을 수 있어 재조회로 상태 재확인
      getKioskList();
    }
  };

  return {
    kiosks,
    doorMessage,
    setDoorMessage,
    keepOpenLoading,
    doorLoading,
    getKioskList,
    handleOpenDoor,
    handleToggleAlwaysOpen,
  };
};

export default useKioskControl;
